import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BookNeedSchema,
  CreateQuoteSchema,
  RecordQuoteSchema,
  RequestQuotesSchema,
} from '@peles/shared';
import * as needsApi from '../api/needs';

export const needsKey = (eventId: string) => ['needs', eventId] as const;
export const quotesKey = (needId: string) => ['quotes', needId] as const;

/** The Plan view's needs, with their stage, quotes and money. */
export function useEventNeeds(eventId: string | undefined) {
  return useQuery({
    queryKey: needsKey(eventId ?? ''),
    queryFn: () => needsApi.getEventNeeds(eventId!),
    enabled: !!eventId,
  });
}

export function useNeedQuotes(needId: string | undefined) {
  return useQuery({
    queryKey: quotesKey(needId ?? ''),
    queryFn: () => needsApi.listQuotes(needId!),
    enabled: !!needId,
  });
}

/**
 * Anything done to a quote may book its need, price its items and make its
 * vendor one of the event's — so the quotes, the expense lines (which refresh
 * the needs and the items) and the event's vendor links are all refreshed.
 */
function useQuoteMutation<TVars, TResult>(
  needId: string,
  mutationFn: (vars: TVars) => Promise<TResult>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: quotesKey(needId) });
      qc.invalidateQueries({ queryKey: ['expenses'] });
      qc.invalidateQueries({ queryKey: ['entity-links'] });
    },
  });
}

export function useRequestQuotes(needId: string) {
  return useQuoteMutation(
    needId,
    (body: Omit<RequestQuotesSchema, 'includeItems'> & { includeItems?: boolean }) =>
      needsApi.requestQuotes(needId, body),
  );
}

export function useCreateQuote(needId: string) {
  return useQuoteMutation(needId, (body: CreateQuoteSchema) => needsApi.createQuote(needId, body));
}

export function useRecordQuote(needId: string) {
  return useQuoteMutation(
    needId,
    ({ quoteId, body }: { quoteId: string; body: RecordQuoteSchema }) =>
      needsApi.recordQuote(quoteId, body),
  );
}

export function useAcceptQuote(needId: string) {
  return useQuoteMutation(
    needId,
    ({ quoteId, notifyOthers }: { quoteId: string; notifyOthers: boolean }) =>
      needsApi.acceptQuote(quoteId, { notifyOthers }),
  );
}

export function useDeclineQuote(needId: string) {
  return useQuoteMutation(needId, (quoteId: string) => needsApi.declineQuote(quoteId));
}

export function useWithdrawQuote(needId: string) {
  return useQuoteMutation(needId, (quoteId: string) => needsApi.withdrawQuote(quoteId));
}

export function useBookNeed(needId: string) {
  return useQuoteMutation(needId, (body: BookNeedSchema) => needsApi.bookNeed(needId, body));
}
